import { lazy, Suspense } from "react";
import { Navigate } from "react-router";

import { useSession } from "@/api/queries/auth";
import { PageHeader, PageLoader, Section } from "@/components/ui";
import { useTenant } from "@/hooks/useTenant";
import { isFieldFirst } from "@/pages/field/work/access";

import { RecentClientsLink, RecentClientsWidget, ShortcutsWidget, WhatsNewLink, WhatsNewWidget } from "./dashboard/widgets/HomeWidgets";

const DashboardPage = lazy(() => import("./dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })));

export const DASHBOARD = "dashboard.view_dashboard";

/**
 * The workspace home: the dashboard for people whose role has it, My work for field people
 * (installers, subcontractors), the simple home page for everyone else.
 */
export function HomePage() {
  const { can } = useTenant();
  if (isFieldFirst(can)) return <Navigate to="field/work" replace />;
  if (can(DASHBOARD)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <DashboardPage />
      </Suspense>
    );
  }
  return <SimpleHome />;
}

/** A simple, useful landing surface, built from the same widgets as the dashboard. */
export function SimpleHome() {
  const { context, can } = useTenant();
  const { data: session } = useSession();
  const firstName = session?.user?.first_name || session?.user?.email;

  return (
    <div className="px-6 py-6">
      <PageHeader
        eyebrow={context.tenant.name}
        title={`Welcome, ${firstName}`}
        description={context.groups.length ? <>Your roles: {context.groups.map((g) => g.name).join(", ")}</> : undefined}
      />
      <div className="grid gap-4 @4xl:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-4">
          <Section title="Start here">
            <ShortcutsWidget />
          </Section>
          {can("clients.view_client") && (
            <Section title="Recently updated clients" actions={<RecentClientsLink />}>
              <RecentClientsWidget />
            </Section>
          )}
        </div>
        <Section title="What's new" actions={<WhatsNewLink />}>
          <WhatsNewWidget />
        </Section>
      </div>
    </div>
  );
}
